import { platformApiClient } from './platform-api-client';
import type { PlatformUser, PlatformLoginResponse } from '@/types/platform-auth.types';

export const platformAuthService = {
  login: (email: string, password: string): Promise<PlatformLoginResponse> =>
    platformApiClient
      .post<PlatformLoginResponse>('/platform/auth/login', { email, password })
      .then((r) => r.data),

  getMe: (accessToken?: string): Promise<PlatformUser> =>
    platformApiClient
      .get<PlatformUser>('/platform/auth/me', {
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : undefined,
      })
      .then((r) => r.data),

  /**
   * Phase 12.2A (F-01) — logout now actually revokes the server-side session.
   *
   * This previously posted with NO body, while the backend requires
   * `PlatformRefreshDto { refreshToken }`. Every call therefore returned 400,
   * the caller swallowed it, and the refresh token stayed valid for its full
   * 7-day life while the user believed they had signed out. It also meant the
   * platform logout audit event added in Phase 12.1C could never fire.
   *
   * The refresh token is read from the store by the caller and passed here; the
   * existing revocation architecture is reused unchanged (single-token revoke,
   * `revokedReason: 'logout'`). No new logout mechanism, no token-lifetime
   * change.
   *
   * Rejections are NOT swallowed here — the caller must be able to tell a real
   * server-side logout from a failed one.
   */
  logout: (refreshToken: string): Promise<void> =>
    platformApiClient.post('/platform/auth/logout', { refreshToken }).then(() => undefined),

  refresh: (refreshToken: string): Promise<PlatformLoginResponse> =>
    platformApiClient
      .post<PlatformLoginResponse>('/platform/auth/refresh', { refreshToken })
      .then((r) => r.data),

  /**
   * Phase 12.8 — self-service password change over the EXISTING SP-15 endpoint.
   *
   * No backend change accompanies this: `POST /platform/auth/change-password`
   * has been deployed since Phase 12.1B and was simply unreachable from the
   * console. The subject is resolved server-side from the bearer token
   * (`user.platformUserId`) and is never sent — there is deliberately no `id`
   * parameter here, because adding one would invent a contract the server does
   * not accept.
   *
   * The route is `@PlatformAuthenticatedOnly()`, so it carries no permission:
   * every platform role may rotate its own credential, including the auditor,
   * who holds no manage permission at all.
   *
   * **A success destroys the caller's session.** The server bumps
   * `tokenVersion`, stamps `passwordChangedAt` and revokes every refresh token,
   * so the access token in flight is dead the moment this resolves. Callers
   * must clear auth and send the user back to the login page rather than
   * continue — see `PlatformProfilePage`.
   */
  changePassword: (currentPassword: string, newPassword: string): Promise<{ message: string }> =>
    platformApiClient
      .post<{ message: string }>('/platform/auth/change-password', {
        currentPassword,
        newPassword,
      })
      .then((r) => r.data),
};
